import { migrateDiceCandidate } from './candidatesApi';
import { resolveDiceProfileId } from './diceCandidateMapper';

export function normalizeSourceKey(value) {
  if (value && typeof value === 'object') {
    return normalizeSourceKey(value.value ?? value.key ?? value.id ?? value.label ?? value.name);
  }

  return String(value ?? '').trim().toLowerCase();
}

export function getSearchSources(value) {
  return (Array.isArray(value) ? value : [value])
    .flat()
    .map(normalizeSourceKey)
    .filter((item, index, source) => item && item !== 'all' && source.indexOf(item) === index);
}

export function resolveSearchSource(value, fallback = 'internal') {
  const sources = getSearchSources(value);
  if (sources.includes('internal')) return 'internal';
  const normalizedFallback = normalizeSourceKey(fallback);
  if (sources[0]) return sources[0];
  if (normalizedFallback) return normalizedFallback;
  return fallback === '' ? '' : 'internal';
}

function toTitle(value) {
  return String(value ?? '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getTabKey(tab) {
  return normalizeSourceKey(tab?.key ?? tab?.value ?? tab?.id ?? tab?.source ?? tab?.name ?? tab?.title ?? tab?.label);
}

function getTabCount(tab) {
  const count = tab?.count ?? tab?.countValue;
  const number = Number(count);
  return Number.isFinite(number) ? number : undefined;
}

function getSourceCount(source, responseCounts, currentCounts, activeSource, activeCount) {
  // Both endpoints report a count for EVERY requested source, not just the one
  // being viewed — module-data-list in `count.internal`/`count.dice`, boolean
  // search in the same shape. So the live response wins for any tab; restricting
  // this to the active source left the other tab showing a stale cached figure
  // (or none) even though the number was sitting right there in the response.
  const responseCount = Number(responseCounts?.[source]);
  if (Number.isFinite(responseCount)) return responseCount;

  if (source === activeSource) {
    const activeTotal = Number(activeCount);
    if (Number.isFinite(activeTotal)) return activeTotal;
  }

  const currentCount = currentCounts.get(source);
  if (currentCount !== undefined) return currentCount;

  return undefined;
}

export function buildSourceTabs({
  sources = [],
  activeSource = 'internal',
  activeCount = 0,
  responseCounts = {},
  currentTabs = [],
  backendTabs = [],
} = {}) {
  const currentCounts = new Map(
    (Array.isArray(currentTabs) ? currentTabs : [])
      .map((tab) => [getTabKey(tab), getTabCount(tab)])
      .filter(([source, count]) => source && count !== undefined),
  );

  const sourceMap = new Map();

  // Pass 1 — the CONFIGURED sources define the order. Seeding from backendTabs
  // instead put whichever tab was active first, because the backend only ever
  // returns a tab for the active source — so the strip reordered on every
  // switch. A Map keeps insertion order, and set() on an existing key updates
  // the value without moving it, which is what makes pass 2 order-safe.
  const selectedSources = getSearchSources(sources);
  (selectedSources.length ? selectedSources : [activeSource]).forEach((source) => {
    if (!source || sourceMap.has(source)) return;
    const count = getSourceCount(source, responseCounts, currentCounts, activeSource, activeCount);
    sourceMap.set(source, {
      key: source,
      title: toTitle(source),
      ...(count !== undefined ? { count } : {}),
    });
  });

  // Pass 2 — the backend contributes a real label and count, never a position.
  // Falling back to the previous count keeps a known figure on screen instead
  // of blanking it out the moment you switch away from that tab.
  (Array.isArray(backendTabs) ? backendTabs : []).forEach((tab) => {
    const key = getTabKey(tab);
    if (!key) return;
    const previous = sourceMap.get(key);
    const count = getTabCount(tab) ?? previous?.count ?? currentCounts.get(key);
    sourceMap.set(key, {
      key,
      title: tab.title ?? tab.label ?? tab.name ?? previous?.title ?? toTitle(key),
      ...(count !== undefined ? { count } : {}),
    });
  });

  return Array.from(sourceMap.values());
}

function collectSourceValues(record) {
  return [
    record?.sourceType,
    record?.profileSource,
    record?.selectedSource,
    record?.source,
    record?.provider,
    record?.customFields?.sourceType,
    record?.customFields?.profileSource,
    record?.customFields?.source,
    record?.customFields?.provider,
  ].flat().map(normalizeSourceKey).filter(Boolean);
}

function recordMatchesSource(record, sourceKey, marker) {
  const normalizedSource = normalizeSourceKey(sourceKey);
  if (!normalizedSource) return false;
  if (collectSourceValues(record).includes(normalizedSource)) return true;
  return typeof marker === 'function' ? marker(record) : false;
}

const SOURCE_ACTIONS = [
  {
    source: 'dice',
    field: '__sourceAction_dice_migrate',
    label: 'Action',
    buttonLabel: 'Migrate',
    completedLabel: 'Migrated',
    successMessage: 'Candidate migrated successfully.',
    errorMessage: 'Failed to migrate candidate.',
    isRecordFromSource: (record) => Boolean(record?.customFields?.diceProfileData),
    getRecordId: resolveDiceProfileId,
    execute: migrateDiceCandidate,
  },
];

export function getAvailableSourceActions({ rows = [], selectedSource = '' } = {}) {
  return SOURCE_ACTIONS.filter((action) => {
    const selectedMatches = normalizeSourceKey(selectedSource) === action.source;
    return selectedMatches || rows.some((record) => recordMatchesSource(record, action.source, action.isRecordFromSource));
  });
}

export function sourceActionAppliesToRecord(action, record, selectedSource = '') {
  return normalizeSourceKey(selectedSource) === action.source
    || recordMatchesSource(record, action.source, action.isRecordFromSource);
}

export function getSourceActionInstanceKey(action, record) {
  const id = action?.getRecordId?.(record);
  return id ? `${action.source}:${action.field}:${id}` : '';
}

export async function runSourceAction(action, record) {
  const id = action?.getRecordId?.(record);
  if (!id) throw new Error(`${action?.buttonLabel ?? 'Action'} ID is required`);
  return action.execute(id, record);
}
